import {Checkbox, TextField} from "@mui/material";
import {defineMessage, MessageDescriptor} from "react-intl";
import {useMemo, useState} from "react";
import {
    FormattedFormRow,
    FormContainer,
    FormDialogActionButtons,
    MultipleLineError,
    DialogActions,
    DialogContent,
} from "@dvrt/graphql-widgets";
import CreateAnotherCheckBox from "../../CreateAnotherCheckBox";
import {useForm} from "@/utils/Form";
import ErrorHelper from "../../../utils/ErrorHelper";
import {showNotification} from "@/actions";
import {Link} from "../../widgets";
import TargetSelector from "@/components/TargetSelector";
import {GetCallbackDataQuery, useCreateGatewayMutation} from "../queries.generated";
import EndpointCreateDialog, {EndpointLimitInfo, QuotaInfo} from "@/components/EndpointCreateDialog";
import {gatewayCallbackDataToTargets} from "../common";

type ContentFormProps = {
    domainId: number;
    onSuccess: () => void;
    close: () => void;
    quotaInfo?: QuotaInfo;
    successItemPath: string;
    callbackData: GetCallbackDataQuery;
    sipUser: string;
    sipPassword: string;
};

export type CreateGatewayDialogProps = {
    title: MessageDescriptor;
    isOpen: boolean;
} & ContentFormProps;

const DialogForm = ({
    domainId,
    onSuccess,
    close,
    quotaInfo,
    successItemPath,
    callbackData,
    sipUser,
    sipPassword,
}: ContentFormProps) => {
    const form = useForm(
        useCreateGatewayMutation,
        "createGateway",
        {
            domainId,
            e164: "",
            shortNumber: "",
            displayName: "",
            sipUser: sipUser || "",
            sipPassword: sipPassword || "",
            enabled: true,
            // using 0 as "none" to force graphql to send this value instead of null or undefined
            // to avoid "Int was provided instead of Int!"
            // enforcing a required error on the field instead
            callbackNumberId: 0,
        },
        (_form) =>
            showNotification(
                defineMessage({
                    id: "gateway_created",
                    defaultMessage: "Gateway {link} has been successfully created.",
                }),
                "success",
                {
                    link: (
                        <Link
                            to={`/domains/${domainId}/${successItemPath}/${_form.result.data.createGateway.id}/settings`}
                        >
                            {_form.values.displayName}
                        </Link>
                    ),
                },
                10000,
            ),
    );
    const errorHelper = new ErrorHelper(form.errors);
    const [createAnother, setCreateAnother] = useState(false);

    function handleClose() {
        close();
        form.reset();
    }

    async function submit() {
        if (!(await form.submit())) return;
        onSuccess();

        if (createAnother) form.reset();
        else handleClose();
    }

    const callbackTargets = useMemo(() => gatewayCallbackDataToTargets(callbackData), [callbackData]);

    return (
        <>
            <DialogContent>
                <FormContainer>
                    <EndpointLimitInfo quotaInfo={quotaInfo} />
                    <FormattedFormRow label={defineMessage({id: "e164", defaultMessage: "E.164"})}>
                        <TextField
                            variant="standard"
                            margin="dense"
                            value={form.values.e164}
                            onChange={(event) => form.updateValues({e164: event.target.value})}
                            error={errorHelper.hasError("e164")}
                            helperText={<MultipleLineError errors={errorHelper.fieldErrors("e164")} />}
                        />
                    </FormattedFormRow>
                    <FormattedFormRow label={defineMessage({id: "identifier", defaultMessage: "Identifier"})} required>
                        <TextField
                            variant="standard"
                            margin="dense"
                            value={form.values.shortNumber}
                            onChange={(event) => form.updateValues({shortNumber: event.target.value})}
                            error={errorHelper.hasError("shortNumber")}
                            helperText={<MultipleLineError errors={errorHelper.fieldErrors("shortNumber")} />}
                        />
                    </FormattedFormRow>
                    <FormattedFormRow label={defineMessage({id: "displayName", defaultMessage: "Name"})} required>
                        <TextField
                            variant="standard"
                            margin="dense"
                            value={form.values.displayName}
                            onChange={(event) => form.updateValues({displayName: event.target.value})}
                            error={errorHelper.hasError("displayName")}
                            helperText={<MultipleLineError errors={errorHelper.fieldErrors("displayName")} />}
                        />
                    </FormattedFormRow>
                    <FormattedFormRow label={defineMessage({id: "sip_user", defaultMessage: "SIP user"})} required>
                        <TextField
                            variant="standard"
                            margin="dense"
                            value={form.values.sipUser}
                            onChange={(event) => form.updateValues({sipUser: event.target.value})}
                            error={errorHelper.hasError("sipUser")}
                            helperText={<MultipleLineError errors={errorHelper.fieldErrors("sipUser")} />}
                        />
                    </FormattedFormRow>
                    <FormattedFormRow
                        label={defineMessage({id: "sip_password", defaultMessage: "SIP password"})}
                        required
                    >
                        <TextField
                            variant="standard"
                            margin="dense"
                            value={form.values.sipPassword}
                            onChange={(event) => form.updateValues({sipPassword: event.target.value})}
                            error={errorHelper.hasError("sipPassword")}
                            helperText={<MultipleLineError errors={errorHelper.fieldErrors("sipPassword")} />}
                        />
                    </FormattedFormRow>
                    <FormattedFormRow label={defineMessage({id: "enabled", defaultMessage: "Enabled"})}>
                        <Checkbox
                            value={form.values.enabled}
                            checked={form.values.enabled}
                            onChange={(event) => form.updateValues({enabled: event.target.checked})}
                            color="primary"
                        />
                    </FormattedFormRow>
                    <FormattedFormRow
                        label={defineMessage({id: "callback_number_id", defaultMessage: "Callback number"})}
                        required
                    >
                        <TargetSelector
                            handleChange={(event) => form.updateValues({callbackNumberId: Number(event)})}
                            variant="standard"
                            selected={form.values.callbackNumberId}
                            targets={callbackTargets}
                            errors={errorHelper.fieldErrors("callbackNumberId")}
                            required
                        />
                    </FormattedFormRow>
                </FormContainer>
            </DialogContent>
            <DialogActions>
                <CreateAnotherCheckBox state={createAnother} setState={setCreateAnother} />
                <FormDialogActionButtons
                    mainAction={submit}
                    secondaryAction={() => form.reset()}
                    cancel={close}
                    mainButtonEnabled={form.hasChanged}
                    secondaryButtonEnabled={form.hasChanged}
                />
            </DialogActions>
        </>
    );
};

const CreateGatewayDialog = ({
    title,
    isOpen,
    close,
    domainId,
    onSuccess,
    quotaInfo,
    successItemPath,
    callbackData,
    sipUser,
    sipPassword,
}: CreateGatewayDialogProps) => (
    <EndpointCreateDialog title={title} isOpen={isOpen} closeDialog={close} quotaInfo={quotaInfo}>
        <DialogForm
            domainId={domainId}
            onSuccess={onSuccess}
            close={close}
            quotaInfo={quotaInfo}
            successItemPath={successItemPath}
            callbackData={callbackData}
            sipUser={sipUser}
            sipPassword={sipPassword}
        />
    </EndpointCreateDialog>
);

export default CreateGatewayDialog;
